<template>
  <div v-if="refresh">
    <McButton
      variant="secondary"
      :text="$t('global.viewSetting')"
      icon="icon-outline_layout"
      :disabled="loading"
      @click="openModal"
    />

    <b-modal v-model="showModal" size="md" modal-class="modal-manage-view">
      <div class="wrapper p-2">
        <div class="section section-information">
          <p class="mc-general__label">
            {{ $t('global.text.showBasedOn') }}
          </p>
          <div class="mb-4">
            <McRadioButton
              id="motionstatus"
              key="motionstatus"
              v-model="displayType"
              name="displayType"
              initialValue="motionstatus"
              class="mb-2"
              :label="$t('report.report.vehicleStatus')"
            />

            <McRadioButton
              v-if="
                hasAccessAll([
                  'fleet-task-instant:read',
                  'fleet-task-instant:create',
                  'fleet-task-instant:update',
                  'fleet-task-instant:delete',
                ])
              "
              id="fleetTask"
              key="fleetTask"
              v-model="displayType"
              name="displayType"
              initialValue="fleetTask"
              class="mb-2"
              :label="$t('controlTower.detail.fleetTask')"
            />

            <McRadioButton
              id="fleetgroup"
              key="fleetgroup"
              v-model="displayType"
              name="displayType"
              initialValue="fleetgroup"
              :label="$t('report.fields.vehicleGroup')"
              class="mb-2"
            />

            <McRadioButton
              id="all"
              key="all"
              v-model="displayType"
              name="displayType"
              class="mb-2"
              initialValue="all"
              :label="$t('report.fields.withoutGrouping')"
            />
          </div>
        </div>
        <div class="section section-information">
          <p class="mc-general__label">
            {{ $t('tower.showSensors') }}
          </p>
          <b-alert show variant="primary" class="section-information__content p-2">
            <p class="mb-3">
              {{ $t('global.text.setDisplayDesc') }}
            </p>
            <p class="teal-300">
              <McCheckbox v-model="alwaysChecked" /> {{ $t('tower.clickShow') }}
            </p>
            <p v-if="draggable" class="teal-300">
              {{ $t('global.click') }} <i class="icon-outline_move"></i>
              {{ $t('global.text.toMoveColumn') }}
            </p>
          </b-alert>
        </div>
        <div v-if="hasGrouping" class="section section-group-view">
          <p class="mc-general__label">
            {{ $t('global.text.tableView') }}
          </p>
          <div class="section-group-view__content">
            <div class="section-group-view__header d-flex justify-content-start p-3">
              <div class="d-flex gap-2">
                <McCheckbox v-model="groupTable" />
                <span class="mc-general__label">{{ $t('global.text.groupByFleet') }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="section section-column-manager">
          <div class="section-column-manager__content">
            <div
              :class="`section-column-manager__header d-flex justify-content-${
                max ? 'end' : 'between'
              } p-3`"
            >
              <div v-if="!max" class="d-flex gap-2">
                <McCheckbox
                  v-model="showAllColumn"
                  :disabled="isAnyLocked"
                  @change="handleChangeCheckAll"
                />
                <span class="mc-general__label">{{ $t('global.text.showAllColumn') }}</span>
              </div>
              <button class="mc-primary" @click="handleResetColumn">
                {{ $t('global.resetAsset') }}
              </button>
            </div>
            <draggable
              v-if="draggable"
              v-model="fieldsTemp"
              class="section-column-manager__draggable mb-3"
              :move="checkHeader"
            >
              <transition-group tag="ul">
                <li
                  v-for="(field, index) in fieldsTemp"
                  :key="`${index}-${field.key}`"
                  class="section-column-manager__draggable-item"
                  :disabled="disabledCheck(field)"
                >
                  <div class="d-flex justify-content-between">
                    <div class="d-flex align-items-center gap-2">
                      <i v-if="!field.isSticky" class="icon-outline_move"></i>
                      <div v-if="!field.isSticky" class="__divider"></div>
                      <span class="d-flex gap-2 w-100">
                        <McCheckbox
                          v-model="field.shown"
                          :disabled="disabledCheck(field)"
                          @change="($event) => handleChangeParentCheckbox({ field, value: $event })"
                        />
                        {{ field.label }}
                      </span>
                    </div>
                    <div v-if="refreshAccordion" class="mc-hover-pointer">
                      <i
                        v-if="field.fields && !field.closeChild"
                        class="toggle-list icon-outline_chevron-down"
                        @click="hideChildren(field)"
                      ></i>
                      <i
                        v-if="field.fields && field.closeChild"
                        class="toggle-list icon-outline_chevron-up"
                        @click="hideChildren(field)"
                      ></i>
                    </div>
                  </div>

                  <template v-if="field.fields && !field.closeChild">
                    <hr />

                    <draggable v-model="field.fields" :move="checkHeader">
                      <transition-group tag="ul">
                        <li
                          v-for="item in field.fields"
                          :key="item.key"
                          class="section-column-manager__draggable-sub-item"
                        >
                          <div class="d-flex align-items-center gap-2">
                            <i v-if="!item.isSticky" class="icon-outline_move"></i>
                            <div v-if="!item.isSticky" class="__divider"></div>
                            <span class="d-flex gap-2 w-100">
                              <McCheckbox
                                v-model="item.shown"
                                :disabled="item.isLocked"
                                @change="
                                  ($event) => handleChangeChildCheckbox({ field, value: $event })
                                "
                              />
                              {{ item.label }}
                            </span>
                          </div>
                        </li>
                      </transition-group>
                    </draggable>
                  </template>
                </li>
              </transition-group>
            </draggable>
            <div v-else class="section-column-manager__not-draggable mb-3">
              <ul>
                <li
                  v-for="(field, index) in fieldsTemp"
                  :key="`${index}-${field.key}`"
                  class="section-column-manager__not-draggable-item"
                >
                  <div class="d-flex justify-content-between">
                    <div class="d-flex align-items-center gap-2">
                      <span class="d-flex gap-2 w-100">
                        <McCheckbox
                          v-model="field.shown"
                          :disabled="disabledCheck(field)"
                          @change="($event) => handleChangeParentCheckbox({ field, value: $event })"
                        />
                        {{ field.label }}
                      </span>
                    </div>
                    <div v-if="refreshAccordion" class="mc-hover-pointer">
                      <i
                        v-if="field.fields && !field.closeChild"
                        class="toggle-list icon-outline_chevron-down"
                        @click="hideChildren(field)"
                      ></i>
                      <i
                        v-if="field.fields && field.closeChild"
                        class="toggle-list icon-outline_chevron-up"
                        @click="hideChildren(field)"
                      ></i>
                    </div>
                  </div>

                  <template v-if="field.fields && !field.closeChild">
                    <hr />

                    <ul>
                      <li
                        v-for="item in field.fields"
                        :key="item.key"
                        class="section-column-manager__not-draggable-sub-item"
                      >
                        <div class="d-flex align-items-center gap-2">
                          <span class="d-flex gap-2 w-100">
                            <McCheckbox
                              v-model="item.shown"
                              :disabled="item.isLocked"
                              @change="
                                ($event) => handleChangeChildCheckbox({ field, value: $event })
                              "
                            />
                            {{ item.label }}
                          </span>
                        </div>
                      </li>
                    </ul>
                  </template>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <template #modal-header>
        <span class="__title"> {{ $t('global.viewSetting') }} </span>
      </template>
      <template #modal-footer="{ cancel }">
        <b-row class="modal__footer mx-0">
          <b-col sm="12" md="12" class="modal__footer-btn">
            <McButton
              variant="secondary"
              class="me-2"
              :text="$t('global.action.cancel')"
              @click="cancel"
            />
            <McButton variant="primary" :text="$t('global.action.apply')" @click="handleAccept" />
          </b-col>
        </b-row>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import McButton from '@/components/McButton'
import McCheckbox from '@/components/McCheckbox'
import draggable from 'vuedraggable'
import cloneDeep from 'lodash/cloneDeep'
import McRadioButton from '@/components/McRadioButton'
import { MixPanel } from '@/mixin/mixpanel'

export default {
  components: {
    McButton,
    McCheckbox,
    draggable,
    McRadioButton,
  },
  mixins: [MixPanel],
  props: {
    defaultColumnOrder: {
      type: Array,
      required: true,
    },
    currentColumnOrder: {
      type: Array,
      required: true,
    },
    draggable: {
      type: Boolean,
      required: false,
      default: true,
    },
    max: {
      type: Number,
      required: false,
      default: 0,
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    hasGrouping: {
      type: Boolean,
      required: false,
      default: false,
    },
    groupTableValue: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['setColumn'],
  data() {
    return {
      showModal: false,
      fieldsTemp: [],
      alwaysChecked: true,
      refresh: true,
      refreshAccordion: true,
      groupTable: false,
      displayType: 'motionstatus',
    }
  },
  computed: {
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
    }),
    showAllColumn() {
      if (!this.draggable) {
        return this.fieldsTemp
      }
      return this.fieldsTemp.every((field) => field.shown)
    },
    isAnyLocked() {
      return this.defaultColumnOrder.every(
        (field) =>
          field.isLocked || (field.children && field.children.every((child) => child.isLocked))
      )
    },
  },
  watch: {
    showModal(newValue) {
      if (!newValue) return
      this.fieldsTemp = cloneDeep(this.currentColumnOrder)

      if (newValue) {
        const savedState = localStorage.getItem('viewSettingState')
        if (savedState) {
          const state = JSON.parse(savedState)
          this.fieldsTemp = this.currentColumnOrder.map((field) => {
            const savedField = state.fields.find((f) => f.key === field.key)
            return savedField ? { ...field, ...savedField } : field
          })
          this.groupTable = state.groupTable
          this.displayType = state.displayType
        } else {
          this.fieldsTemp = cloneDeep(this.currentColumnOrder)
        }
      }
    },
    async alwaysChecked() {
      await this.$nextTick()
      this.alwaysChecked = true
    },
    '$i18n.locale': function () {
      this.refresh = false

      setTimeout(() => {
        this.refresh = true
      }, 500)
    },
  },

  mounted() {
    if (this.hasGrouping) {
      this.groupTable = this.groupTableValue
    }
  },

  methods: {
    openModal() {
      this.showModal = !this.showModal
      this.track('Open Atur Tampilan on Control Tower V2')
    },
    disabledCheck(field) {
      if (!this.max && !field.isLocked) return false

      const checked = this.fieldsTemp
        .filter((x) => x.shown && x.key !== 'fleetTaskNumber')
        .map((x) => x.key)
      return (
        this.max &&
        field.key !== 'fleetTaskNumber' &&
        (field.isLocked || (checked.length === this.max && !checked.includes(field.key)))
      )
    },
    handleAccept() {
      const currentState = {
        fields: cloneDeep(this.fieldsTemp),
        groupTable: this.groupTable,
        displayType: this.displayType,
      }
      localStorage.setItem('viewSettingState', JSON.stringify(currentState))

      this.$emit('setDisplay', this.displayType)
      this.$emit('setColumn', cloneDeep(this.fieldsTemp))
      this.$emit('setColumnReport', cloneDeep(this.fieldsTemp))
      this.$emit('setField', this.fieldsTemp)
      this.$emit('setGroupView', this.groupTable)
      this.showModal = false
      this.track('Terapkan Atur Tampilan on Control Tower V2')
    },
    handleResetColumn() {
      this.fieldsTemp = cloneDeep(this.defaultColumnOrder)
    },
    handleChangeCheckAll(value) {
      this.fieldsTemp = this.fieldsTemp.map((field) => {
        if (field.fields) {
          field.fields = field.fields.map((subField) => ({
            ...subField,
            shown: value,
          }))
        }

        return {
          ...field,
          shown: field.isLocked ? true : value,
        }
      })
    },
    checkHeader(value) {
      let indexSticky = []

      if (!Object.prototype.hasOwnProperty.call(value.draggedContext.element, 'parent')) {
        this.fieldsTemp.forEach((field, index) => {
          if (field.isSticky) indexSticky.push(index)
        })
      }

      if (
        value.draggedContext.element.isSticky ||
        indexSticky.includes(value.draggedContext.futureIndex)
      )
        return false

      return true
    },

    handleChangeParentCheckbox({ field, value }) {
      if (!field.fields) return

      field.fields.forEach((item) => {
        item.shown = value
      })
    },
    handleChangeChildCheckbox({ field, value }) {
      if (field.shown == value) return

      let totalCount = 0

      field.fields.forEach((item) => {
        if (item.shown !== value) return

        totalCount++
      })

      if (value === false && totalCount !== field.fields.length) return

      field.shown = value
    },

    hideChildren(field) {
      if (field.closeChild == undefined) field.closeChild = false

      this.refreshAccordion = false

      setTimeout(() => {
        field.closeChild = !field.closeChild
        this.refreshAccordion = true
      }, 50)
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(.mc-button-wrapper) {
  white-space: nowrap;
}
:deep(.modal-manage-view) {
  .modal-dialog {
    border-radius: 0px;
    max-height: 100vh;
    height: 100%;
    margin: 0;
    margin-left: auto;
  }

  .modal-content {
    height: 100%;
  }

  .modal-content,
  .modal-header,
  .modal-footer {
    border-radius: 0;

    .modal__footer-btn {
      display: flex;
    }
  }

  .modal-header {
    color: $white;
    justify-content: space-between;

    .__reset {
      font-size: 13px;
      text-align: right;
      cursor: pointer;
    }
  }

  .modal-body {
    overflow: hidden;
  }

  .wrapper {
    overflow-y: hidden;
    display: flex;
    flex-direction: column;
    height: 100%;
  }
}

.section {
  :deep(input[type='checkbox']) {
    margin-bottom: 2px;
  }

  & > p {
    color: $teal-300;
    font-weight: 600;
  }

  &-information {
    &__content {
      border: 2px solid $blue-100;
      border-radius: 4px;
      margin-bottom: 20px;

      & > p {
        display: flex;
        gap: 5px;
        align-items: center;
        margin-bottom: 0;

        &:first-child {
          margin-bottom: 4px;
        }
      }
    }
  }

  &-group-view {
    flex: 1 1 auto;

    &__content {
      box-shadow: 0px 5px 15px 0px rgba(13, 68, 145, 0.08);
      overflow-y: hidden;
      margin-bottom: 20px;
    }

    &__header {
      button {
        background: transparent;
        border: none;
        color: $cyan;
        font-weight: 600;
      }
    }
  }

  &-column-manager {
    overflow: hidden;
    flex: 1 1 auto;

    &__content {
      box-shadow: 0px 5px 15px 0px rgba(13, 68, 145, 0.08);
      overflow-y: hidden;
      height: 100%;
    }

    &__header {
      button {
        background: transparent;
        border: none;
        color: $cyan;
        font-weight: 600;
      }
    }

    &__draggable {
      background: $gray;
      padding: 16px;
      overflow-y: auto;
      height: 100%;
      padding-bottom: 86px;

      ul {
        padding: 0;
      }

      &-item,
      &-sub-item {
        list-style-type: none;
        cursor: move;
        background: white;
        margin-bottom: 16px;
        border-radius: 5px;
        padding: 14px 12px;
        transition: 0.3s;

        .__divider {
          background-color: $teal-100;
          height: 16px;
          width: 1px;
        }

        .toggle-list {
          font-size: 20px;

          &::before {
            color: #3d69a7;
          }
        }
      }

      &-sub-item {
        list-style-type: none;
        border: 1px solid #cfdae9;
      }
    }

    &__not-draggable {
      background: $gray;
      padding: 16px;
      overflow-y: auto;
      height: 100%;
      padding-bottom: 86px;

      ul {
        padding: 0;
      }

      &-item,
      &-sub-item {
        list-style-type: none;
        background: white;
        margin-bottom: 16px;
        border-radius: 5px;
        padding: 14px 12px;
        transition: 0.3s;

        .toggle-list {
          font-size: 20px;

          &::before {
            color: #3d69a7;
          }
        }
      }

      &-sub-item {
        list-style-type: none;
        border: 1px solid #cfdae9;
      }
    }
  }
}

.icon-outline_move {
  color: $blue-400;
  font-size: 16px;
}

.mc-hover-pointer {
  &:hover {
    cursor: pointer !important;
  }
}
</style>
